/**
 * Call-Off Configuration Tab
 *
 * Per-supplier templating screen — feature flags, supplier-facing labels,
 * and the configurable Direct Delivery sources list. Surfaces the same
 * fields the mockup (mockups/call-off-templating.html) walked through.
 *
 * Visible only when the active supplier has a CallOffSupplierConfig row
 * (i.e. at least one customer relationship has call_off_enabled=True). The
 * empty state is shown otherwise so admins know how to get there.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Loader2, Plus, Trash2, ArrowUp, ArrowDown, PackageCheck, Tags, Truck, Info } from 'lucide-react';
import { toast } from 'sonner';
import { callOffAPI } from '../../../lib/api';
import type { CallOffSupplierConfig, CallOffDirectDeliverySource } from '../../../lib/api';
import { resetCallOffConfigCache } from '../../../hooks/useCallOffConfig';

type EditableSource = CallOffDirectDeliverySource & { _draftId?: string };

const newDraftId = () => `draft-${Math.random().toString(36).slice(2, 10)}`;

export function CallOffConfigTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<CallOffSupplierConfig | null>(null);
  const [sources, setSources] = useState<EditableSource[]>([]);

  const reload = async () => {
    setLoading(true);
    try {
      const res = await callOffAPI.getSupplierConfig();
      setConfig(res.data);
      setSources(res.data.direct_delivery_sources.map((s) => ({ ...s })));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load configuration';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const updateField = <K extends keyof CallOffSupplierConfig>(key: K, value: CallOffSupplierConfig[K]) => {
    if (!config) return;
    setConfig({ ...config, [key]: value });
  };

  const saveSettings = async () => {
    if (!config?.id) return;
    setSaving(true);
    try {
      const payload: Partial<CallOffSupplierConfig> = {
        direct_delivery_enabled: config.direct_delivery_enabled,
        polish_factory_subtab_enabled: config.polish_factory_subtab_enabled,
        primary_warehouse_label: config.primary_warehouse_label,
        primary_warehouse_column_label: config.primary_warehouse_column_label,
        routing_origin: config.routing_origin,
        direct_delivery_modal_title: config.direct_delivery_modal_title,
        source_label_singular: config.source_label_singular,
        source_label_plural: config.source_label_plural,
      };
      const res = await callOffAPI.updateSupplierConfig(config.id, payload);
      // The PATCH response is the model serializer payload, which omits
      // `persisted`. We know the row exists because we just updated it,
      // so keep persisted=true and retain the in-memory sources list.
      setConfig({
        ...res.data,
        persisted: true,
        direct_delivery_sources: config.direct_delivery_sources,
      });
      resetCallOffConfigCache();
      toast.success('Configuration saved');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save configuration';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  // ----- sources -----

  const addSourceDraft = () => {
    setSources((prev) => [
      ...prev,
      {
        id: null,
        _draftId: newDraftId(),
        code: '',
        name: '',
        customer_match_token: '',
        is_active: true,
        sort_order: prev.length,
      },
    ]);
  };

  const updateSource = (key: number | string, patch: Partial<EditableSource>) => {
    setSources((prev) => prev.map((s) => (matches(s, key) ? { ...s, ...patch } : s)));
  };

  const moveSource = (key: number | string, dir: -1 | 1) => {
    setSources((prev) => {
      const idx = prev.findIndex((s) => matches(s, key));
      const target = idx + dir;
      if (idx < 0 || target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next.map((s, i) => ({ ...s, sort_order: i }));
    });
  };

  const removeSource = async (key: number | string) => {
    const target = sources.find((s) => matches(s, key));
    if (!target) return;
    if (target.id && config?.id) {
      try {
        await callOffAPI.deleteDirectDeliverySource(config.id, target.id);
        toast.success(`Removed ${target.code || 'source'}`);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to delete source';
        toast.error(message);
        return;
      }
    }
    setSources((prev) => prev.filter((s) => !matches(s, key)));
  };

  const saveSources = async () => {
    if (!config?.id) return;
    setSaving(true);
    try {
      // Persist additions and edits in display order
      const updated: CallOffDirectDeliverySource[] = [];
      for (let i = 0; i < sources.length; i++) {
        const s = sources[i];
        const payload = {
          code: s.code,
          name: s.name,
          customer_match_token: s.customer_match_token,
          is_active: s.is_active,
          sort_order: i,
        };
        if (s.id) {
          const res = await callOffAPI.updateDirectDeliverySource(config.id, s.id, payload);
          updated.push(res.data);
        } else {
          if (!s.code.trim() || !s.name.trim()) continue; // skip empty drafts
          const res = await callOffAPI.addDirectDeliverySource(config.id, payload);
          updated.push(res.data);
        }
      }
      // Re-issue an explicit reorder to align IDs with current display order
      await callOffAPI.reorderDirectDeliverySources(
        config.id,
        updated.filter((s) => !!s.id).map((s) => s.id as number),
      );
      setSources(updated.map((s) => ({ ...s })));
      resetCallOffConfigCache();
      toast.success('Sources saved');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save sources';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  // ----- render -----

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!config?.persisted || !config.id) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><PackageCheck className="h-5 w-5" />Call-Off Configuration</CardTitle>
          <CardDescription>Per-supplier templating for labels, feature flags, and direct-delivery sources.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground space-y-2">
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-foreground">No configuration row for this supplier yet.</p>
                <p className="mt-1">A configuration row is created automatically when Call-Off is enabled on a customer–supplier relationship. Contact your platform administrator if you need Call-Off enabled for a customer.</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><PackageCheck className="h-5 w-5" />Call-Off Configuration</CardTitle>
          <CardDescription>
            Templating for {config.supplier_name || 'this supplier'} — feature flags, labels, and the direct-delivery source list.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Feature flags */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg"><PackageCheck className="h-5 w-5" />Feature Flags</CardTitle>
          <CardDescription>Toggle which Call-Off capabilities are available for this supplier.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ToggleRow
            icon={<Truck className="h-5 w-5 text-primary" />}
            label="Direct Delivery enabled"
            description='Show "Add Direct Delivery" entry in the Call-Off wizard.'
            checked={config.direct_delivery_enabled}
            onChange={(v) => updateField('direct_delivery_enabled', v)}
          />
          <ToggleRow
            icon={<PackageCheck className="h-5 w-5 text-primary" />}
            label="Polish Factory subtab enabled"
            description="Show the Polish Factory subtab inside Stock Planning."
            checked={config.polish_factory_subtab_enabled}
            onChange={(v) => updateField('polish_factory_subtab_enabled', v)}
          />
        </CardContent>
      </Card>

      {/* Labels & Routing */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg"><Tags className="h-5 w-5" />Labels &amp; Routing</CardTitle>
          <CardDescription>Supplier-facing copy. The same Call-Off engine renders different terminology per client.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <LabelInput
            label="Primary warehouse label"
            hint='e.g. "Saltney", "Main DC"'
            value={config.primary_warehouse_label}
            onChange={(v) => updateField('primary_warehouse_label', v)}
          />
          <LabelInput
            label="Primary warehouse column header"
            hint="Shown in Stock Planning grid"
            value={config.primary_warehouse_column_label}
            onChange={(v) => updateField('primary_warehouse_column_label', v)}
          />
          <LabelInput
            label="Routing origin"
            hint='Empty hides the "Plan Route" button'
            value={config.routing_origin}
            onChange={(v) => updateField('routing_origin', v)}
          />
          <LabelInput
            label="Direct delivery modal title"
            value={config.direct_delivery_modal_title}
            onChange={(v) => updateField('direct_delivery_modal_title', v)}
          />
          <LabelInput
            label="Source label (singular)"
            hint='e.g. "Factory", "Hub"'
            value={config.source_label_singular}
            onChange={(v) => updateField('source_label_singular', v)}
          />
          <LabelInput
            label="Source label (plural)"
            value={config.source_label_plural}
            onChange={(v) => updateField('source_label_plural', v)}
          />
        </CardContent>
        <CardContent>
          <Button onClick={saveSettings} disabled={saving} className="w-full">
            {saving ? 'Saving...' : 'Save flags & labels'}
          </Button>
        </CardContent>
      </Card>

      {/* Direct Delivery Sources — only shown when Direct Delivery is enabled */}
      {config.direct_delivery_enabled && (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg"><Truck className="h-5 w-5" />Direct Delivery Sources</CardTitle>
              <CardDescription>Replaces the hardcoded factory list. Each row maps a code to a name and matches incoming customers by token.</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={addSourceDraft}>
              <Plus className="h-4 w-4 mr-1" />Add source
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {sources.length === 0 ? (
            <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
              No sources configured. Click <strong>Add source</strong> to get started.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
                    <th className="px-2 py-2 w-20">Order</th>
                    <th className="px-2 py-2 w-28">Code</th>
                    <th className="px-2 py-2">Name</th>
                    <th className="px-2 py-2">Customer match token</th>
                    <th className="px-2 py-2 w-20">Active</th>
                    <th className="px-2 py-2 w-14" />
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {sources.map((s, idx) => {
                    const key = s.id ?? s._draftId!;
                    return (
                      <tr key={key}>
                        <td className="px-2 py-2">
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" disabled={idx === 0} onClick={() => moveSource(key, -1)} title="Move up">
                              <ArrowUp className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" disabled={idx === sources.length - 1} onClick={() => moveSource(key, 1)} title="Move down">
                              <ArrowDown className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          <Input className="h-8" value={s.code} onChange={(e) => updateSource(key, { code: e.target.value })} placeholder="PF1" />
                        </td>
                        <td className="px-2 py-2">
                          <Input className="h-8" value={s.name} onChange={(e) => updateSource(key, { name: e.target.value })} placeholder="DREWAR" />
                        </td>
                        <td className="px-2 py-2">
                          <Input className="h-8" value={s.customer_match_token} onChange={(e) => updateSource(key, { customer_match_token: e.target.value })} placeholder="DREWAR" />
                        </td>
                        <td className="px-2 py-2">
                          <Switch checked={s.is_active} onCheckedChange={(v) => updateSource(key, { is_active: v })} />
                        </td>
                        <td className="px-2 py-2">
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeSource(key)} title="Delete">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-4">
            <Button onClick={saveSources} disabled={saving} className="w-full">
              {saving ? 'Saving...' : 'Save sources'}
            </Button>
          </div>
        </CardContent>
      </Card>
      )}
    </div>
  );
}

function matches(s: EditableSource, key: number | string): boolean {
  if (typeof key === 'number') return s.id === key;
  return s._draftId === key;
}

interface ToggleRowProps {
  icon: React.ReactNode;
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function ToggleRow({ icon, label, description, checked, onChange }: ToggleRowProps) {
  return (
    <div className="flex items-center justify-between p-4 border rounded-lg">
      <div className="flex items-center gap-3">
        {icon}
        <div>
          <Label className="text-base font-medium">{label}</Label>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

interface LabelInputProps {
  label: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
}

function LabelInput({ label, hint, value, onChange }: LabelInputProps) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} />
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
